import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Plus, Search, Phone, PhoneCall, Mail, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useContacts, useCreateContact, type Contact } from '../api/contacts'
import { formatPhone, isValidEmail, normalizePhone, timeAgo, toE164 } from '../lib/utils'
import { PhoneInput } from '../components/ui/PhoneInput'
import { SpinLoader } from '../components/shared/SpinLoader'
import { ErrorCard } from '../components/shared/ErrorCard'
import { Pagination } from '../components/shared/Pagination'
import { useDebouncedValue } from '../hooks/useDebounce'

const LIMIT = 20

export function ContactsPage() {
  const { t } = useTranslation('contacts')
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 300)
  const [offset, setOffset] = useState(0)
  const [showAddForm, setShowAddForm] = useState(false)
  const navigate = useNavigate()

  const { data, isLoading, isError, refetch } = useContacts({
    search: debouncedSearch || undefined,
    limit: LIMIT,
    offset,
  })

  const createContact = useCreateContact()

  // Debounced search — `useDebouncedValue` handles timer setup/teardown.
  const handleSearch = (value: string) => {
    setSearch(value)
    setOffset(0)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-text-primary md:text-2xl">{t('list.title')}</h1>
          <p className="mt-1 text-sm text-text-secondary">{t('list.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          {t('list.addContact')}
        </button>
      </div>

      {/* Add Contact Form */}
      {showAddForm && (
        <AddContactForm
          onSubmit={async (data) => {
            await createContact.mutateAsync(data)
            setShowAddForm(false)
          }}
          onCancel={() => setShowAddForm(false)}
          isLoading={createContact.isPending}
          error={createContact.error?.message}
        />
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
        <input
          type="text"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={t('list.searchPlaceholder')}
          className="w-full rounded-lg border border-border bg-surface py-2 pl-10 pr-4 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      {/* Content */}
      {isLoading && <SpinLoader label={t('list.loading')} />}

      {isError && <ErrorCard message={t('list.loadFailed')} onRetry={() => refetch()} />}

      {data && data.contacts.length === 0 && (
        <div className="rounded-lg border border-border bg-surface-card p-8 text-center">
          <User className="mx-auto h-10 w-10 text-text-muted" />
          <p className="mt-3 text-sm text-text-muted">
            {debouncedSearch ? t('list.emptyNoMatch') : t('list.emptyNoContacts')}
          </p>
        </div>
      )}

      {data && data.contacts.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-lg border border-border bg-surface-card md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-text-muted">
                  <th className="px-4 py-3">{t('list.table.name')}</th>
                  <th className="px-4 py-3">{t('list.table.phone')}</th>
                  <th className="px-4 py-3">{t('list.table.email')}</th>
                  <th className="px-4 py-3">{t('list.table.updated')}</th>
                  <th className="px-4 py-3">{t('list.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.contacts.map((contact) => (
                  <ContactRow key={contact.id} contact={contact} onClick={() => navigate(`/app/contacts/${contact.id}`)} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-2 md:hidden">
            {data.contacts.map((contact) => (
              <ContactCard key={contact.id} contact={contact} onClick={() => navigate(`/app/contacts/${contact.id}`)} />
            ))}
          </div>

          <Pagination offset={offset} limit={LIMIT} total={data.total} onPageChange={setOffset} />
        </>
      )}
    </div>
  )
}

function ContactRow({ contact, onClick }: { contact: Contact; onClick: () => void }) {
  const { t } = useTranslation('contacts')
  const navigate = useNavigate()
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer border-b border-border/50 transition-colors hover:bg-surface-elevated/50"
    >
      <td className="px-4 py-3 font-medium text-text-primary">
        {contact.name || <span className="text-text-muted italic">{t('list.noName')}</span>}
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1.5 font-mono text-text-secondary">
          <Phone className="h-3.5 w-3.5" />
          {formatPhone(contact.phone)}
        </span>
      </td>
      <td className="px-4 py-3 text-text-secondary">
        {contact.email ? (
          <span className="inline-flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5" />
            {contact.email}
          </span>
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-text-muted">{timeAgo(contact.updated_at)}</td>
      <td className="px-4 py-3">
        <button
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/app/calls/stage/${encodeURIComponent(contact.phone)}?contactId=${contact.id}`)
          }}
          className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-primary/10 hover:text-primary"
          title={t('list.stageCall')}
        >
          <PhoneCall className="h-4 w-4" />
        </button>
      </td>
    </tr>
  )
}

function ContactCard({ contact, onClick }: { contact: Contact; onClick: () => void }) {
  const { t } = useTranslation('contacts')
  const navigate = useNavigate()
  return (
    <div
      onClick={onClick}
      className="cursor-pointer rounded-lg border border-border bg-surface-card p-4 transition-colors hover:bg-surface-elevated/50"
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-text-primary">
          {contact.name || <span className="text-text-muted italic">{t('list.noName')}</span>}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/app/calls/stage/${encodeURIComponent(contact.phone)}?contactId=${contact.id}`)
            }}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-primary/10 hover:text-primary"
            title={t('list.stageCall')}
          >
            <PhoneCall className="h-4 w-4" />
          </button>
          <span className="text-xs text-text-muted">{timeAgo(contact.updated_at)}</span>
        </div>
      </div>
      <div className="mt-1 flex items-center gap-1.5 font-mono text-sm text-text-secondary">
        <Phone className="h-3.5 w-3.5" />
        {formatPhone(contact.phone)}
      </div>
      {contact.email && (
        <div className="mt-1 flex items-center gap-1.5 text-sm text-text-secondary">
          <Mail className="h-3.5 w-3.5" />
          {contact.email}
        </div>
      )}
    </div>
  )
}

function AddContactForm({
  onSubmit,
  onCancel,
  isLoading,
  error,
}: {
  onSubmit: (data: { phone: string; name?: string; email?: string; notes?: string }) => void
  onCancel: () => void
  isLoading: boolean
  error?: string
}) {
  const { t } = useTranslation('contacts')
  const [phone, setPhone] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [notes, setNotes] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)

  const phoneComplete = normalizePhone(phone).length === 10
  const emailInvalid = email.trim().length > 0 && !isValidEmail(email.trim())
  const canSubmit = phoneComplete && !emailInvalid

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    onSubmit({
      phone: toE164(phone),
      name: name.trim(),
      email: email.trim(),
      notes: notes.trim(),
    })
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3"
    >
      <h3 className="text-sm font-semibold text-text-primary">{t('form.title')}</h3>
      {error && <p className="text-sm text-error">{error}</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <PhoneInput
          value={phone}
          onChange={setPhone}
          placeholder={t('form.phonePlaceholder')}
          required
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('form.namePlaceholder')}
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <div className="flex flex-col gap-1">
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            placeholder={t('form.emailPlaceholder')}
            aria-invalid={emailTouched && emailInvalid}
            className={`rounded-lg border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 ${
              emailTouched && emailInvalid
                ? 'border-error focus:border-error focus:ring-error'
                : 'border-border focus:border-primary focus:ring-primary'
            }`}
          />
          {emailTouched && emailInvalid && (
            <p className="text-xs text-error">{t('form.emailInvalid')}</p>
          )}
        </div>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('form.notesPlaceholder')}
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isLoading || !canSubmit}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {isLoading ? t('form.creating') : t('form.create')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-border px-4 py-2 text-sm text-text-secondary transition-colors hover:bg-surface-elevated"
        >
          {t('form.cancel')}
        </button>
      </div>
    </form>
  )
}
